import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  Snackbar,
  Typography,
  Paper,
  Button,
} from "@mui/material";
import DynamicChartViewer from "./DynamicChartViewer";
import DownloadIcon from "@mui/icons-material/Download";
import { fadeIn } from "../../../core/constants/fadeIn";
import { CHART_TYPES } from "../../../core/constants/chartTypes";
import KpiCard from "./KpiCard";
import SchemaEditor from "./SchemaEditor";
import AnalyzeInsightsPanel, {
  isGenericAnalyzeText,
} from "./AnalyzeInsightsPanel";
import { parseFaqs, toInsightItems } from "../../../core/utils/analyzeInsights";

const MessageList = ({
  messages,
  onSchemaUpdate,
  darkMode,
  disabled = false,
  onSuggestedQuestion,
}) => {
  const ref = useRef();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    ref.current?.scrollTo({
      top: ref.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const glassBorder = darkMode
    ? "rgba(255, 255, 255, 0.12)"
    : "rgba(0, 0, 0, 0.08)";
  const glassBg = darkMode
    ? "rgba(255, 255, 255, 0.03)"
    : "rgba(0, 0, 0, 0.02)";
  const textColor = darkMode ? "#cbd5e1" : "#475569";

  return (
    <Box
      ref={ref}
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 7,
      }}
    >
      {messages.map((m, idx) => {
        const insightItems = toInsightItems(m.meta?.insights);
        const faqs = parseFaqs(m.meta?.faqs);
        const hasCharts =
          Array.isArray(m.meta?.charts) && m.meta.charts.length > 0;
        const hasInsightsUi = insightItems.length > 0 || faqs.length > 0;
        const showText =
          !!m.text &&
          (!hasInsightsUi || !isGenericAnalyzeText(m.text)) &&
          !hasCharts;
        const insightsOnly =
          m.role === "assistant" && hasInsightsUi && !hasCharts && !showText;

        return (
        <Box
          key={idx}
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-end",
            justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            animation: `${fadeIn} 0.5s ease-out`,
          }}
        >
          {m.role === "assistant" && (
            <Avatar
              sx={{ bgcolor: "#0b74ff", width: 32, height: 32, color: "white" }}
            >
              T
            </Avatar>
          )}

          <Box
            sx={{
              maxWidth:
                m.role === "assistant" ? "95%" : { xs: "100%", sm: "75%" },
              width: m.role === "assistant" ? "100%" : "auto",
              display: "inline-block",
            }}
          >
            <Box
              sx={{
                p: insightsOnly ? 0 : m.role === "assistant" ? 2.25 : 1.5,
                borderRadius: "20px",
                bgcolor: insightsOnly
                  ? "transparent"
                  : m.role === "user"
                    ? "#0b74ff"
                    : "rgba(255,255,255,0.05)",
                color: m.role === "user" ? "#fff" : "inherit",
                whiteSpace: "pre-line",
                width: m.role === "assistant" ? "100%" : "fit-content",
                maxWidth: "100%",
                border:
                  m.role === "assistant" && !insightsOnly
                    ? `1px solid ${glassBorder}`
                    : "none",
              }}
            >
              {m.meta?.preface && (
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: 13,
                    mb: 1,
                    color: darkMode ? "#38bdf8" : "#0369a1",
                  }}
                >
                  {`Yes, Got it — ${m.meta.preface}`}
                </Typography>
              )}

              {showText && (
                <Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: hasInsightsUi ? 1.5 : 0 }}>
                  {m.text}
                </Typography>
              )}

              {hasCharts && (
                <Box
                  sx={{
                    mt: showText ? 2 : 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    {m.meta.charts.some(
                      (c) => c.toLowerCase() === "kpi card",
                    ) && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {m.meta.data?.map((item, i) => {
                          const key = Object.keys(item)[0];
                          return (
                            <KpiCard
                              key={i}
                              title={item.title || key}
                              value={item.value ?? item[key]}
                              trend={item.trend}
                              colorIndex={i}
                              darkMode={darkMode}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {m.meta.charts.filter((c) =>
                      CHART_TYPES.includes(c.toLowerCase()),
                    ).length > 0 && (
                      <DynamicChartViewer
                        possibleCharts={m.meta.charts}
                        data={m.meta.data}
                        xAxis={m.meta.xAxis}
                        yAxis={m.meta.yAxis}
                        title={m.meta.title}
                        darkMode={darkMode}
                      />
                    )}
                  </Box>
              )}

              {hasInsightsUi && (
                <Box sx={{ mt: hasCharts || showText ? 2 : 0 }}>
                  <AnalyzeInsightsPanel
                    insights={m.meta.insights}
                    faqs={m.meta.faqs}
                    fileName={
                      m.meta.displayFileName ||
                      m.meta.originalFileName ||
                      m.meta.fileName
                    }
                    darkMode={darkMode}
                    disabled={disabled}
                    onSuggestedQuestion={onSuggestedQuestion}
                  />
                </Box>
              )}
            </Box>

            {m.meta?.schema?.columns?.length > 0 && !m.meta?.hideSchemaEditor && (
              <SchemaEditor
                schema={m.meta.schema}
                userSessionFileId={m.meta.userSessionFileId}
                fileName={m.meta.fileName}
                onConfirm={onSchemaUpdate}
                darkMode={darkMode}
              />
            )}

            {m.meta?.summary && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: glassBg,
                  border: `1px solid ${glassBorder}`,
                  backdropFilter: "blur(4px)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: textColor,
                    whiteSpace: "pre-line",
                    fontSize: 13,
                  }}
                >
                  {m.meta.summary}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    borderColor: glassBorder,
                    color: textColor,
                    "&:hover": {
                      borderColor: "#4f46e5",
                      bgcolor: "rgba(79, 70, 229, 0.05)",
                    },
                  }}
                  onClick={() => {
                    const blob = new Blob([m.meta.summary], {
                      type: "text/plain",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "dataset_report.txt";
                    a.click();
                  }}
                >
                  Download Full Report
                </Button>
              </Paper>
            )}
          </Box>

          {m.role === "user" && (
            <Avatar sx={{ bgcolor: "#6366f1", width: 32, height: 32 }}></Avatar>
          )}
        </Box>
      );
      })}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1000}
        onClose={() => setSnackbarOpen(false)}
        message="Copied!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default MessageList;
